/** Zustand eines Eintrags, nach dem `GET …/entries?state=` filtert (`EntryState` im BE, NEX-1724). */
export const ENTRY_STATES = ['FRAGILE', 'UNUSED', 'NEVER_HIT', 'MISSING'] as const;
export type EntryState = (typeof ENTRY_STATES)[number];

/** Art des Kandidaten, ueber den der Runner den Eintrag zuletzt gefunden hat. */
export type HitKind = 'TESTID' | 'ID' | 'ROLE' | 'NAME' | 'LABEL' | 'TEXT' | 'CSS' | 'XPATH' | 'POSITION';

/** Ein Testelement mit seinen Objekten, Auswahl fuer Objekt- und Fensterbedingungen. */
export interface ConditionLibrary {
  readonly name: string;
  readonly objects: readonly string[];
}

/**
 * Eine Objektbibliothek mit ihrer Eintragszahl (`ObjectMappingSummary` im BE). Die Zaehler je
 * Zustand liefert das BE immer; optional nur, damit aeltere Antworten nicht brechen.
 */
export interface ObjectMappingSummary {
  id: string;
  name: string;
  engineId: string;
  description: string | null;
  entryCount: number;
  fragileCount?: number;
  unusedCount?: number;
  neverHitCount?: number;
  missingCount?: number;
  /** Tags der Bibliothek (NEX-2271); das BE liefert sie immer. */
  tags?: string[];
  /** Adresse der Seite oder des Fensters, reine Information (NEX-2446); `null` ohne Adresse. */
  address?: string | null;
  /** Letzte Aenderung am Testelement selbst, ISO-8601 (NEX-2446). */
  updatedAt?: string;
}

/** Body von `POST …/object-mappings`; ohne `engineId` legt das BE die Bibliothek fuer `browser` an. */
export interface CreateObjectMappingRequest {
  name: string;
  engineId?: string;
  description?: string;
  /** Ohne Adresse fehlt das Feld (NEX-2446). */
  address?: string;
}

/** Typen, die das BE fuer neue Eintraege annimmt (`ObjectTypes` im BE); zuerst die des Bundles (`OB_TYPEN`). */
export const OBJECT_TYPES = [
  'Button',
  'Eingabe',
  'Link',
  'Bild',
  'Text',
  'Auswahl',
  'Tabelle',
  'Bereich',
  'Seite',
] as const;

/** Ein Kandidat im Schreib-Body; `name` nur bei `role`. */
export interface LocatorInput {
  strategy: string;
  value: string;
  name?: string;
}

/** Body von `POST …/entries`. */
export interface CreateEntryRequest {
  logicalName: string;
  typeName?: string;
  url?: string;
  locators?: LocatorInput[];
  /** Leer oder `null` speichert das BE als `null`, hoechstens 255 Zeichen (NEX-2449). */
  area?: string | null;
}

/** Body von `PUT …/entries/{id}`: Typ, Adresse, Bereich und Kandidaten werden als Ganzes ersetzt. */
export interface UpdateEntryRequest {
  typeName?: string;
  url?: string;
  locators?: LocatorInput[];
  area?: string | null;
}

/** Antwort von `GET …/object-mappings/search`: Namen, die mit dem Suchtext anfangen. */
export interface ObjectMappingSearchResult {
  mappings: { id: string; name: string; engineId: string }[];
  entries: { id: string; mappingId: string; mappingName: string; logicalName: string; reference: string }[];
}

/** Ein Locator-Kandidat, z. B. `{ strategy: 'css', value: '#login' }`. */
export type ObjectMappingLocator = Record<string, string>;

/** Ein logisches Objekt einer Bibliothek (`ObjectMappingEntryView` im BE). */
export interface ObjectMappingEntryView {
  id: string;
  logicalName: string;
  typeName: string | null;
  fulltag: string | null;
  locators: ObjectMappingLocator[];
  url: string | null;
  missingSince?: string | null;
  lastHitAt?: string | null;
  /** 0-basiert. */
  lastHitIndex?: number | null;
  lastHitKind?: HitKind | null;
  usageCount?: number;
  /** Bereich, etwa „iFrame #report“ (NEX-2449); `null` ohne Bereich und im Bestand. */
  area?: string | null;
}

/** Wo im Schritt ein Objektverweis steht (`ObjectRefSource` im BE). */
export type ObjectRefSource = 'TCDL_TARGET' | 'TCDL_CONDITION' | 'STEP_CONDITION';

/** Eine Stelle im Schritt; `lineNumber` und `text` fehlen bei der Bedingung am Schritt. */
export interface UsageLine {
  lineNumber: number | null;
  source: ObjectRefSource;
  reference: string;
  text: string | null;
  /** Zeile nach dem Umbenennen, nur in der Vorschau und im Ergebnis (NEX-1278). */
  newText?: string | null;
}

export interface UsageStep {
  stepId: string;
  stepKey: string;
  stepTitle: string;
  lines: UsageLine[];
}

/** Art des Besitzers einer Stelle (`OwnerKind` im BE, NEX-2449). */
export type OwnerKind = 'TESTCASE' | 'MODULE';

/** Ein Testfall oder Testmodul mit den betroffenen Schritten. */
export interface UsageOwner {
  id: string;
  key: string;
  title: string;
  steps: UsageStep[];
  ownerKind: OwnerKind;
}

/**
 * Ein Objekt, das ein Testschritt anspricht, das im Testelement aber fehlt (`OpenReference` im BE, NEX-2449):
 * je Besitzer eine Zeile.
 */
export interface OpenReference {
  reference: string;
  /** Der Objektname ohne Testelement, so wie er im Schritt steht. */
  entryName: string;
  ownerId: string;
  /** `null` nur im Bestand ohne Kennung. */
  ownerKey: string | null;
  ownerTitle: string;
  ownerKind: OwnerKind;
  stepKey: string | null;
  lineNumber: number | null;
  source: ObjectRefSource;
  /** Die TCDL-Zeile; `null` bei der Bedingung am Schritt. */
  text: string | null;
}

/** Wer ein Objekt benutzt (NEX-1277). */
export interface ObjectUsages {
  occurrenceCount: number;
  testCases: UsageOwner[];
  modules: UsageOwner[];
  unlinkedSteps: UsageStep[];
  /** Nur beim Eintrag, und nur wenn andere Bibliotheken denselben Namen tragen. */
  duplicates?: ObjectDuplicates | null;
}

/**
 * Gleicher Objektname in mehreren Bibliotheken (NEX-1278). Die Kurzform ohne Bibliothek loest auf
 * hoechstens einen davon auf; nur auf ihn beziehen sich Verwender und Umbenennen der Kurzform.
 */
export interface ObjectDuplicates {
  otherMappings: string[];
  assigned: boolean;
  /** Bibliothek, auf deren Eintrag die Kurzform aufloest; null, wenn sie nicht eindeutig ist. */
  resolvedMapping: string | null;
}

/** Vorschau oder Ergebnis einer Umbenennung (NEX-1278), beides in derselben Form. */
export interface RenameResult {
  oldName: string;
  newName: string;
  applied: boolean;
  occurrenceCount: number;
  testCaseCount: number;
  moduleCount: number;
  usages: ObjectUsages;
  notTouched: UsageLine[];
}
